import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useApi } from "../../../../hooks/useApi";
import { useAuth } from "../../../../contexts/AuthContext";
import { getTeamStats } from "../../../../services/goalsPerformanceApi";
import { computeHealth, buildMoveQueue, buildUpdates, flattenInsights } from "./brainSignals.mjs";
import { riskByMember, subtreeStats, orgShape } from "./brainAnalytics.mjs";

// Company Brain server state. Deliberately no new backend: every feed below is
// an endpoint the dashboard/org-chart/brain-ops screens already use, all of
// them hierarchy-scoped server-side (non-admins only ever see themselves plus
// the people below them). This hook only joins and derives.

const fmt = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

const rangeFor = (period) => {
  // A custom window arrives as "custom:YYYY-MM-DD:YYYY-MM-DD" so the period
  // stays a single serialisable value — everything downstream (cache keys, the
  // effect dependency, the URL) keeps working without a second state field.
  if (typeof period === "string" && period.startsWith("custom:")) {
    const [, a, b] = period.split(":");
    const s = new Date(`${a}T00:00:00`);
    const e = new Date(`${b}T00:00:00`);
    if (Number.isFinite(s.getTime()) && Number.isFinite(e.getTime())) {
      s.setHours(0, 0, 0, 0);
      e.setHours(23, 59, 59, 999);
      return e.getTime() < s.getTime() ? { start: e, end: s } : { start: s, end: e };
    }
    // An unparseable custom value falls through to today rather than throwing —
    // a bad URL should not blank the dashboard.
  }
  const end = new Date(); end.setHours(0, 0, 0, 0);
  const start = new Date(end);
  if (period === "7d") start.setDate(end.getDate() - 6);
  if (period === "30d") start.setDate(end.getDate() - 29);
  // "yesterday" is a single closed day: shift BOTH boundaries back one, so the
  // window is yesterday..yesterday rather than yesterday..today. Unlike "today"
  // it contains no partial hours, which makes it the honest comparison period.
  if (period === "yesterday") {
    start.setDate(end.getDate() - 1);
    end.setDate(end.getDate() - 1);
  }
  // `end` must be the LAST instant of its day, not its first. Consumers that
  // send the range as instants (the person dossier / read) would otherwise ask
  // for a zero-width window on "today" and get an empty day back. Consumers
  // that format it as YYYY-MM-DD are unaffected — same calendar day either way.
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

// Find the viewer inside the hierarchy tree (admins get the whole org, so
// their own node may sit anywhere — or nowhere, if they're outside the tree).
const findNode = (nodes, id) => {
  for (const n of nodes || []) {
    if (String(n.id) === String(id)) return n;
    const hit = findNode(n.reports, id);
    if (hit) return hit;
  }
  return null;
};

export function useBrainData() {
  const api = useApi();
  const { user } = useAuth();

  // "today" | "yesterday" | "7d" | "30d" | "custom:<start>:<end>"
  const [period, setPeriod] = useState("today");
  const range = useMemo(() => rangeFor(period), [period]);

  const [hierarchy, setHierarchy] = useState(null); // { hierarchy, levels, totalUsers, scope }
  const [kpi, setKpi] = useState(null);
  const [members, setMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [trend, setTrend] = useState([]);
  const [insightEntries, setInsightEntries] = useState([]);
  const [goals, setGoals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0); // 60s heartbeat → presence + KPIs stay live

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // Hierarchy + project membership: fetch once (both Redis-cached server-side).
  // Membership draws the person↔project threads on the canvas.
  const [memberships, setMemberships] = useState(new Map());
  useEffect(() => {
    let alive = true;
    api.get("/api/v1/users/hierarchy")
      .then((res) => { if (alive) setHierarchy(res.data?.data || null); })
      .catch(() => {});
    api.get("/api/v1/projects/dropdown")
      .then((res) => {
        if (!alive) return;
        const map = new Map();
        for (const p of res.data?.data?.projects || []) {
          map.set(String(p._id), new Set(
            (p.teamMembers || [])
              .map((m) => m.userId?._id || m.userId)
              .filter(Boolean).map(String),
          ));
        }
        setMemberships(map);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [api]);

  /* A window change must put the canvas back into its loading state; the
     heartbeat must NOT. `loading` was only ever set false here, so picking a new
     date silently swapped stale numbers in behind the same rendered orbit and
     the user got no feedback at all that anything was being refetched. */
  const rangeKey = `${+range.start}:${+range.end}`;
  const rangeKeyRef = useRef(rangeKey);

  // Period-scoped feeds. Refetched on the heartbeat so "active now" is honest.
  useEffect(() => {
    let alive = true;
    if (rangeKeyRef.current !== rangeKey) {
      rangeKeyRef.current = rangeKey;
      setLoading(true);
    }
    const qs = new URLSearchParams({ startDate: fmt(range.start), endDate: fmt(range.end) });

    const kpiP = api.get(`/api/v1/live-analytics/team-hours?${qs}`)
      .then((res) => {
        if (!alive) return;
        const h = res.data?.data || {};
        const totalMinutes = h.totalMinutes || 0;
        const idleMinutes = h.totalIdleTimeMinutes || 0;
        const activeMinutes = h.totalActiveTimeMinutes ?? Math.max(0, totalMinutes - idleMinutes);
        setKpi({
          totalMinutes, activeMinutes, idleMinutes,
          teamSize: h.teamSize ?? 0, activeMembers: h.activeMembers ?? 0,
          totalSessions: h.totalSessions ?? 0,
          utilization: totalMinutes ? Math.round((activeMinutes / totalMinutes) * 100) : 0,
        });
      }).catch(() => {});

    const teamP = api.get(`/api/v1/live-analytics/team-analytics-paginated?${qs}&page=1&limit=100&sortOrder=desc`)
      .then((res) => {
        if (!alive) return;
        const rows = res.data?.data?.teamAnalytics || [];
        setMembers(rows.map((m) => ({
          userId: m.userId,
          name: `${m.firstName || ""} ${m.lastName || ""}`.trim() || m.email || "Member",
          position: m.position || "Team member",
          hours: m.hours || "0h 0m",
          totalMinutes: m.totalMinutes || 0,
          activeMinutes: m.activeMinutes || 0,
          idleMinutes: m.idleMinutes || 0,
          sessions: m.sessions || 0,
          lastActivity: m.lastActivity || null,
          streak: m.streak || 0,
          isCurrentUser: m.isCurrentUser || m.userId === user?.id,
          hasData: (m.sessions || 0) > 0 || (m.totalMinutes || 0) > 0,
        })));
      }).catch(() => {});

    const projP = api.get(`/api/v1/live-analytics/project-overview-paginated?${qs}&page=1&limit=50&sortOrder=desc`)
      .then((res) => {
        if (!alive) return;
        const rows = res.data?.data?.projects || [];
        setProjects(rows.map((p) => ({
          _id: p.projectId, name: p.name, status: p.status,
          timeSpent: p.timeSpent || "0h 0m", minutes: p.totalMinutes || 0,
          members: p.teamMembers || 0,
          isActive: p.isActive !== undefined ? p.isActive : p.status === "active",
        })));
      }).catch(() => {});

    // Trend always covers the last 7 days regardless of period — the health
    // score needs direction, and a single "today" bar has none.
    const t = rangeFor("7d");
    const trendP = api.get(`/api/v1/live-analytics/daily-trend?startDate=${fmt(t.start)}&endDate=${fmt(t.end)}`)
      .then((res) => {
        if (!alive) return;
        setTrend((res.data?.data?.series || []).map((r) => ({
          date: r.date, minutes: Math.round(r.minutes || 0), score: Math.round(r.score || 0),
        })));
      }).catch(() => {});

    // AI insights (anomalies + growth ideas). 403 for roles without org
    // intelligence access — the brain simply runs without that feed.
    const insP = api.get(`/api/v1/brain-ops/insights?${qs}`)
      .then((res) => { if (alive) setInsightEntries(flattenInsights(res.data?.data?.insights || {})); })
      .catch(() => {});

    const goalsP = getTeamStats()
      .then((res) => { if (alive && res?.success && res.data) setGoals(res.data); })
      .catch(() => {});

    Promise.allSettled([kpiP, teamP, projP, trendP, insP, goalsP]).then(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api, range, rangeKey, user, tick]);

  // ── Derivations ──
  const memberById = useMemo(() => new Map(members.map((m) => [String(m.userId), m])), [members]);

  // Today's trend point is a partial day — scoring against it would fire a
  // false "pace dropped" alarm every morning. Score on complete days only.
  const scoringTrend = useMemo(() => {
    if (!trend.length) return trend;
    const today = fmt(new Date());
    return trend[trend.length - 1].date === today ? trend.slice(0, -1) : trend;
  }, [trend]);

  const moves = useMemo(() => buildMoveQueue({ members, projects, insightEntries, kpi, trend: scoringTrend }),
    [members, projects, insightEntries, kpi, scoringTrend]);

  // Viewer's slice of the org: direct reportees + how deep it goes below them.
  const { selfNode, directs, indirectCount, isAdminScope } = useMemo(() => {
    const roots = hierarchy?.hierarchy || [];
    const selfId = user?._id || user?.id;
    const self = selfId ? findNode(roots, selfId) : null;
    const adminScope = hierarchy?.scope === "organization" || roots.length > 1;
    // An admin whose own card is a leaf (no reports) would otherwise stare at
    // an empty canvas — fall through to the top-level leaders like an
    // out-of-tree admin gets.
    if (self && ((self.reports || []).length > 0 || !adminScope)) {
      return {
        selfNode: self,
        directs: self.reports || [],
        indirectCount: Math.max(0, (self.count || 0) - (self.reports?.length || 0)),
        isAdminScope: adminScope,
      };
    }
    // Admin outside the tree: the top-level leaders are their "directs".
    const total = roots.reduce((n, r) => n + 1 + (r.count || 0), 0);
    return { selfNode: null, directs: roots, indirectCount: Math.max(0, total - roots.length), isAdminScope: true };
  }, [hierarchy, user]);

  // Branch rollups + org shape, over the full visible tree (collapsed branches
  // still count). One walk each; hierarchy and members are already in hand.
  const branchStats = useMemo(
    () => subtreeStats(directs, memberById, riskByMember(moves)),
    [directs, memberById, moves]
  );
  const shape = useMemo(() => orgShape(directs), [directs]);

  // Drill-down: clicking a manager on the canvas opens their reports as a
  // sub-orbit. Purely client-side — the whole tree is already in `hierarchy`.
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpand = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(String(id))) next.delete(String(id)); else next.add(String(id));
      return next;
    });
  }, []);

  // People on the canvas: direct reportees joined with their period metrics,
  // plus the reports of anyone the viewer has expanded (recursively).
  const people = useMemo(() => {
    const toPerson = (n, parentId, depth) => {
      const m = memberById.get(String(n.id));
      const total = m ? (m.activeMinutes || 0) + (m.idleMinutes || 0) : 0;
      const idleRatio = total > 0 ? m.idleMinutes / total : 0;
      const branch = branchStats.get(String(n.id));
      return {
        id: String(n.id), name: n.name, position: n.position || n.levelName || "",
        avatar: n.avatar, below: n.count || 0,
        directsCount: (n.reports || []).length,
        joinedAt: n.joinedAt || null,
        teamMinutes: branch?.teamMinutes || 0,
        risk: branch?.risk || 0,
        parentId, depth, expanded: expanded.has(String(n.id)),
        totalMinutes: m?.totalMinutes || 0, hours: m?.hours || "0h 0m",
        activeMinutes: m?.activeMinutes || 0, idleMinutes: m?.idleMinutes || 0,
        sessions: m?.sessions || 0,
        idleRatio, streak: m?.streak || 0, lastActivity: m?.lastActivity || null,
        // live | idle-heavy | quiet | silent — drives the node's status ring.
        // "live" = a session update landed in the DB within the last 10 min.
        status: !m || !m.hasData ? "silent"
          : (m.lastActivity && Date.now() - new Date(m.lastActivity).getTime() < 10 * 60 * 1000) ? "live"
            : idleRatio >= 0.25 ? "idle" : "steady",
      };
    };
    const out = [];
    const walk = (nodes, parentId, depth) => {
      for (const n of nodes || []) {
        out.push(toPerson(n, parentId, depth));
        if (expanded.has(String(n.id))) walk(n.reports, String(n.id), depth + 1);
      }
    };
    walk(directs, null, 0);
    return out;
  }, [directs, memberById, branchStats, expanded]);

  const health = useMemo(() => computeHealth({
    kpi, trend: scoringTrend, projects, goalScore: goals?.teamAverageScore ?? null,
  }), [kpi, scoringTrend, projects, goals]);

  const updates = useMemo(() => buildUpdates({ insightEntries, members }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [insightEntries, members, tick]);

  const refresh = useCallback(() => setTick((n) => n + 1), []);

  return {
    user, period, setPeriod, range, loading, refresh,
    kpi, trend, goals, members, projects, insightEntries, memberships,
    hierarchy, selfNode, directs, people, indirectCount, isAdminScope, shape,
    expanded, toggleExpand,
    health, moves, updates,
  };
}
